import { React, useEffect, useState, useRef } from "react";
import {
  NotesPanelContainer,
  CardContainer,
  CardDate,
  CardHeader,
  CardInfo,
  CardName,
  CardStatus,
  CardText,
  CardsContainer,
  NotesHeader,
  PanelCardsSearchInputContainer,
  PanelCardsSearchInput,
  NotesToggle,
  ToggleOption,
  AddNoteIcon,
  AddNoteButton,
  CardFooter,
  AlertTag,
  AlertField,
  AlertFieldLabel,
} from './NotesPanel.styled';
import addNote from "../../../shared/icons/addNote.svg";
import bluePen from "../../../shared/icons/bluePen.svg";
import blueTrash from "../../../shared/icons/blueTrash.svg";
import { Search } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Select, MenuItem, Box } from "@mui/material";
import { AddNoteForm } from "./AddNoteForm";
import { useDeleteNoteMutation, useGetNotesMutation, useGetNotesTypesQuery, useGetUserInfoQuery } from "../../../services/ClientService";
import { useParams } from "react-router-dom";
import useFetchWithMsal from "../../../utils/useFetchWithMsal";
import moment from "moment";
import { LoadingPanel } from "../../../shared/components/LoadingPanel";
import { EmptyMessage } from "../TableTab.styled";

// Keyword rules: normalize and match against all text in a note, then check the Formik field.
const ALERT_FIELD_RULES = [
  { keywords: ['levelofcare', 'level of care', 'level_of_care', 'levelofcareid'], field: 'levelOfCareId' },
  { keywords: ['status', 'clientstatus', 'statusid'], field: 'statusId' },
  { keywords: ['housing', 'clienthousing', 'clienthousingid'], field: 'clientHousingId' },
  { keywords: ['companybranch', 'company branch', 'branchid', 'companybranchid'], field: 'companyBranchId' },
  { keywords: ['counselor', 'counselorid', 'assigned counselor'], field: 'counselorId' },
  { keywords: ['birthdate', 'birthday', 'date of birth', 'dob'], field: 'birthDate' },
  { keywords: ['insurance'], field: 'insurance' },
  { keywords: ['assignment', 'no assignment', 'clientassignment', 'clientassignments'], field: 'clientAssignments' },
  { keywords: ['intake not complete', 'intakenotcomplete', 'intake assessment'], field: 'intakeDetails',
    isSatisfied: (v) => Boolean(v) && v !== 'Intake Not Complete' },
];

const isAlertResolvedByFormValues = (note, formValues) => {
  if (!formValues) return false;
  const norm = (s) => (s || '').toLowerCase().replace(/[\s_\-]+/g, '');
  const noteText = norm(
    [note.alertCode, note.message, note.details, note.text].filter(Boolean).join(' ')
  );
  if (!noteText) return false;
  const isSet = (v) => {
    if (Array.isArray(v)) return v.length > 0;
    return v !== null && v !== undefined && v !== '' && v !== 0;
  };

  for (const rule of ALERT_FIELD_RULES) {
    if (rule.keywords.some(kw => noteText.includes(norm(kw)))) {
      const val = formValues[rule.field];
      if (val !== undefined) {
        if (rule.isSatisfied) return rule.isSatisfied(val);
        return typeof val === 'object' && !Array.isArray(val) ? Boolean(val) : isSet(val);
      }
    }
  }

  if (note.alertCode) {
    const camel = note.alertCode
      .replace(/[-_\s]+(.)/g, (_, c) => c.toUpperCase())
      .replace(/^[A-Z]/, c => c.toLowerCase());
    const val = formValues[camel] ?? formValues[note.alertCode];
    if (val !== undefined) return isSet(val);
  }
  return false;
};

export const NotesPanel = (props) => {
  const {userNotes, clientIdForm, formValues, initialEditNote} = props
  const { id: clientIdParam } = useParams();
  const { skip } = useFetchWithMsal();
  const { setVisibility, type, onToggleChange } = props;
  const [searchValue, setSearchValue] = useState('');
  const [notes, setNotes] = useState([]);
  const [toggleOption, setToggleOption] = useState('latest');

  const handleToggle = (option) => {
    setToggleOption(option);
    if (onToggleChange) onToggleChange(option);
  };
  const [openAddNoteForm, setOpenAddNoteForm] = useState(false);
  const [editNoteInfo, setEditNoteInfo] = useState();
  const [selectedNoteType, setSelectedNoteType] = useState('all');
  
  const clientId = clientIdForm ?? clientIdParam

  const [getNotes, { data, isLoading }] = useGetNotesMutation({
    clientId: clientId,
    recordNumber: 999,
    offset: 0 
  }, {skip})

  const { data: types } = useGetNotesTypesQuery({ skip });
  const { data: basicInfo } = useGetUserInfoQuery({ skip });
  const [deleteNote, error] = useDeleteNoteMutation();


  useEffect(() => {
      getNotes({
        clientId: clientId,
        recordNumber: 999,
        offset: 0,
        searchText: searchValue 
      })
  }, [clientId, searchValue]);

  useEffect(() => {
    setNotes(data?.items)
  }, [data]);

  const addNewElement = (message, clientId, type, ownerId) => {
    
    const newArray = [...notes];

    const newElement = {
        id: newArray.length + 1,
        text: message,
        createDate: new Date().toISOString(),
        clientId: clientId,
        quickNoteTypeId: type,
        ownerId: basicInfo?.id,
        owner: {
            firstName: basicInfo?.firstName,
            lastName: basicInfo?.lastName
        }
    };

    newArray.unshift(newElement);

    setNotes(newArray);
  }

  const removeNote = (id) => {
    
    const newArray = [...notes];
    const indexToRemove = newArray.findIndex(item => item.id === id);
    
    if (indexToRemove !== -1) {
        newArray.splice(indexToRemove, 1);
    }

    setNotes(newArray);
}


  const closeForm = (message, clientId, type, ownerId) => {
    if(editNoteInfo) {
      setEditNoteInfo(null)
    }
    if(message && clientId && type) {
      addNewElement(message, clientId, type, ownerId)
    }
    setOpenAddNoteForm(false)
  }

  const getNameById = id => {
    const foundItem = types?.find(item => item.id === id);

    return foundItem ? foundItem.name : null;
  }

  // System note types (IsSystemType) are read-only. Fall back to id 9 ("System note")
  // if the flag isn't present on the loaded types.
  const systemTypeIds = (types ?? []).filter((t) => t.isSystemType).map((t) => t.id);
  const isSystemType = (id) => systemTypeIds.includes(id) || id === 9;
  // A note opens read-only when it's a system type, or it isn't owned by the current user
  // (non-owned notes were never editable — the pencil was already hidden for them).
  const isNoteReadOnly = (note) =>
    isSystemType(note?.quickNoteTypeId) || note?.ownerId !== basicInfo?.id;

  // Clicking a note card opens it: editable for the user's own regular notes,
  // read-only for system-type (or non-owned) notes. Alerts stay display-only.
  const openNote = (note) => {
    if (!note || note.type === 'Alert') return;
    setOpenAddNoteForm(false);
    setEditNoteInfo({
      text: note.text,
      quickNoteTypeId: note.quickNoteTypeId,
      id: note.id,
      ownerId: note.ownerId,
      requestStatus: note.quickNoteRequestSatatusId,
      readOnly: isNoteReadOnly(note),
    });
  };

  // When opened from the client grid with a specific note, open that note once the
  // list has loaded (so we can resolve the full record's owner/type reliably).
  const openedInitialRef = useRef(null);
  useEffect(() => {
    if (!initialEditNote?.id) return;
    if (openedInitialRef.current === initialEditNote.id) return;
    const list = notes ?? [];
    const match = list.find((n) => n.id === initialEditNote.id);
    if (!match && list.length === 0) return; // wait for the list to load
    openedInitialRef.current = initialEditNote.id;
    openNote(match || initialEditNote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditNote?.id, notes, basicInfo?.id, types]);

  const setDeleteNote = (ownerId, quickNoteTypeId, id) => {
    deleteNote({data: { quickNoteTypeId: quickNoteTypeId, ownerId: ownerId }, noteId: id});
    removeNote(id)
  }

  return (
    <NotesPanelContainer type={type} toggleOption={toggleOption}>
      {isLoading && <LoadingPanel/>}
      <NotesHeader>
        {toggleOption === 'show-all' && (
          <PanelCardsSearchInputContainer>
            <PanelCardsSearchInput
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              placeholder='Search'
            />
            <Search/>
          </PanelCardsSearchInputContainer>
        )}
        <NotesToggle>
          <ToggleOption
          onClick={() => handleToggle('latest')}
            active={toggleOption === 'latest'}
          >
            Latest Notes & Alerts
          </ToggleOption>
          <ToggleOption
            onClick={() => handleToggle('show-all')}
            active={toggleOption === 'show-all'}
          >
            Show All
          </ToggleOption>
        </NotesToggle>
        <IconButton
          size="small"
          onClick={() => setVisibility(false)}
          sx={{ bgcolor: '#E5EAF2', '&:hover': { bgcolor: '#DAE2ED' }, flexShrink: 0 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </NotesHeader>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, px: 1.25, gap: 1 }}>
        {!openAddNoteForm && (
          <AddNoteButton onClick={() => setOpenAddNoteForm(true)}>
            <AddNoteIcon src={addNote} alt="" />
            Add Notes & Alerts
          </AddNoteButton>
        )}
        <Select
          size="small"
          value={selectedNoteType}
          onChange={(e) => setSelectedNoteType(e.target.value)}
          sx={{
            ml: 'auto',
            fontSize: '12px',
            minWidth: 130,
            '& .MuiSelect-select': { py: '4px' },
          }}
        >
          <MenuItem value="all" sx={{ fontSize: '12px' }}>All Types</MenuItem>
          <MenuItem value="__alerts__" sx={{ fontSize: '12px' }}>⚠ Alerts</MenuItem>
          {types?.map((t) => (
            <MenuItem key={t.id} value={t.id} sx={{ fontSize: '12px' }}>{t.name}</MenuItem>
          ))}
        </Select>
      </Box>
      {openAddNoteForm && <AddNoteForm closeForm={closeForm} types={types} clientId={clientId} editNoteInfo={editNoteInfo} readOnly={editNoteInfo?.readOnly}/>}
      <CardsContainer style={toggleOption === 'latest' ? { overflowY: 'auto', maxHeight: 'calc(100vh - 230px)', flexDirection: 'column', flexWrap: 'nowrap' } : {}}>
        {notes?.length ? (
          notes
            .filter(note => {
              // Hide alerts whose condition is already satisfied in the current form
              if (note.type === 'Alert' && formValues && isAlertResolvedByFormValues(note, formValues)) return false;
              if (selectedNoteType === 'all') return true;
              if (selectedNoteType === '__alerts__') return note.type === 'Alert';
              return note.type !== 'Alert' && String(note.quickNoteTypeId) === String(selectedNoteType);
            })
            .map((note) => {
              const isAlert = note.type === 'Alert';
              const isEditingThis = editNoteInfo?.id === note.id;
              const clickable = !isEditingThis && !isAlert;
              return <CardContainer
                key={note.id}
                editNoteInfo={isEditingThis}
                isAlert={isAlert}
                onClick={clickable ? () => openNote(note) : undefined}
                style={clickable ? { cursor: 'pointer' } : undefined}
              >
                {editNoteInfo?.id !== note.id ?
                  <>
                    <CardHeader>
                      <CardInfo>
                        <CardName>{note?.owner?.firstName} {note?.owner?.lastName}</CardName>
                        <CardDate>{moment(note?.createDate).format("MM/DD/YYYY HH:mm")}</CardDate>
                      </CardInfo>
                      {isAlert ? (
                        <AlertTag>Alert</AlertTag>
                      ) : (
                        <CardStatus>
                          {getNameById(note.quickNoteTypeId)}
                        </CardStatus>
                      )}
                    </CardHeader>
                    {isAlert ? (
                      <>
                        {note.alertCode && (
                          <AlertField><AlertFieldLabel>Code:</AlertFieldLabel>{note.alertCode}</AlertField>
                        )}
                        {note.alertDate && (
                          <AlertField><AlertFieldLabel>Date:</AlertFieldLabel>{moment(note.alertDate).format("MM/DD/YYYY")}</AlertField>
                        )}
                        {note.message && (
                          <AlertField><AlertFieldLabel>Message:</AlertFieldLabel>{note.message}</AlertField>
                        )}
                        {note.details && (
                          <AlertField><AlertFieldLabel>Details:</AlertFieldLabel>{note.details}</AlertField>
                        )}
                        {note.completedBy && (
                          <AlertField><AlertFieldLabel>Completed By:</AlertFieldLabel>{note.completedBy}</AlertField>
                        )}
                        {!note.alertCode && !note.message && !note.details && note.text && (
                          <CardText>{note.text}</CardText>
                        )}
                      </>
                    ) : (
                      <CardText>
                        {note.text}
                      </CardText>
                    )}
                    {(basicInfo?.id === note.ownerId && note.quickNoteTypeId !== 9) && <CardFooter
                    >
                      <AddNoteIcon src={bluePen} alt="" onClick={(e) => { e.stopPropagation(); openNote(note); }}/>
                      <AddNoteIcon src={blueTrash} alt="" onClick={(e) => { e.stopPropagation(); setDeleteNote(
                        note?.ownerId,
                        note?.quickNoteTypeId,
                        note?.id,
                      ); }}/>
                    </CardFooter>}
                  </> : <AddNoteForm closeForm={closeForm} types={types} clientId={clientId} editNoteInfo={editNoteInfo} readOnly={editNoteInfo?.readOnly}/>}
              </CardContainer>
            })
        ) : <EmptyMessage>No notes to show</EmptyMessage>}
      </CardsContainer>
    </NotesPanelContainer>
  );
}